/**
 * Admin lead service — list, detail, update, and promote operations on
 * captured leads. Separate from the public `leadService.ts` so the public
 * submit / preview surface stays clean.
 *
 * All endpoints assume the caller carries the auth token (the shared `api`
 * axios instance attaches it automatically).
 */
import api from './api';
import type {
  Lead,
  LeadListFilters,
  LeadListResponse,
  LeadStatus,
  PromoteRequest,
  PromoteResponse,
} from '../types/lead';

interface ServiceResult<T> {
  data?: T;
  error?: string;
}

function extractError(error: any, fallback: string): string {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d: any) => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d)))
      .join('; ');
  }
  return error?.message || fallback;
}

/**
 * List leads. The frontend uses `date_from` / `date_to` while the backend
 * accepts `created_after` / `created_before` — translate here.
 */
export async function fetchLeads(
  filters?: LeadListFilters,
): Promise<ServiceResult<LeadListResponse>> {
  try {
    const params: Record<string, any> = {};
    if (filters) {
      if (filters.page !== undefined) params.page = filters.page;
      if (filters.size !== undefined) params.size = filters.size;
      if (filters.persona) params.persona = filters.persona;
      if (filters.tier) params.tier = filters.tier;
      if (filters.status) params.status = filters.status;
      if (filters.date_from) params.created_after = filters.date_from;
      if (filters.date_to) params.created_before = filters.date_to;
      if (filters.search) params.search = filters.search;
    }
    const response = await api.get('/leads', { params });
    const payload = response.data || {};
    return {
      data: {
        leads: payload.leads || [],
        total: payload.total ?? 0,
        page: payload.page ?? 1,
        size: payload.size ?? 20,
        pages: payload.pages ?? 0,
      },
    };
  } catch (error: any) {
    return { error: extractError(error, 'Failed to load leads') };
  }
}

export async function fetchLead(id: number): Promise<ServiceResult<Lead>> {
  try {
    const response = await api.get(`/leads/${id}`);
    return { data: response.data };
  } catch (error: any) {
    return { error: extractError(error, 'Failed to load lead') };
  }
}

export async function updateLead(
  id: number,
  payload: { status?: LeadStatus; notes?: string },
): Promise<ServiceResult<Lead>> {
  try {
    const response = await api.patch(`/leads/${id}`, payload);
    return { data: response.data };
  } catch (error: any) {
    return { error: extractError(error, 'Failed to update lead') };
  }
}

export async function promoteLead(
  id: number,
  payload: PromoteRequest,
): Promise<ServiceResult<PromoteResponse>> {
  try {
    const response = await api.post(`/leads/${id}/promote`, payload);
    return { data: response.data };
  } catch (error: any) {
    return { error: extractError(error, 'Failed to promote lead') };
  }
}

const leadAdminService = {
  fetchLeads,
  fetchLead,
  updateLead,
  promoteLead,
};

export default leadAdminService;
